import { useSuspenseInfiniteQuery } from '@tanstack/react-query'

import type { BusinessSummaryItem } from 'src/features/business/shared/schemas/business-item'
import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'

export type SearchHistoryFilter = 'professional' | 'business' | 'search'

export interface SearchHistoryParams {
  filter: SearchHistoryFilter
  limit?: number
  skip?: number
}

export interface SearchHistoryItem {
  id: number
  userId: number
  rawQuery: string | null
  businessId: number | null
  targetUserId: number | null
  subqueries: Record<string, unknown> | null
  searchSummary: string | null
  lastAccessedAt: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export type SearchHistoryResponse = PublicUserData[] | BusinessSummaryItem[] | SearchHistoryItem[]

const getSearchHistory = async ({
  filter,
  limit = 10,
  skip = 0,
}: SearchHistoryParams): Promise<SearchHistoryResponse> => {
  try {
    const params = new URLSearchParams()
    params.append('offset', skip.toString())
    params.append('limit', limit.toString())

    const response = await getRequest<SearchHistoryResponse>(`/users/history/${filter}?${params.toString()}`)

    if (!response) {
      return []
    }

    if (filter === 'search') {
      return response as SearchHistoryItem[]
    } else if (filter === 'business') {
      return response as BusinessSummaryItem[]
    } else {
      return response as PublicUserData[]
    }
  } catch (error) {
    console.error('Error fetching search history:', error)
    return []
  }
}

export const useSearchHistory = ({ filter, limit = 5, skip = 0 }: SearchHistoryParams) => {
  return useSuspenseInfiniteQuery({
    queryKey: queries.search.history(filter, limit, skip).queryKey,
    queryFn: ({ pageParam }) =>
      getSearchHistory({
        filter,
        limit,
        skip: pageParam as number,
      }),
    initialPageParam: skip,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageItems = lastPage

      if (lastPageItems.length < limit) {
        return undefined
      }

      const loadedCount = allPages.reduce((acc, page) => {
        return acc + page.length
      }, 0)
      return skip + loadedCount
    },
  })
}
